import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import HeroDance from "../assets/hero-dance.webp";
import Pill from "../components/Pill";
import Button from "../components/Buttons/Button";
import CalendarIcon from "../assets/icons/calendar.svg";
import LocationIcon from "../assets/icons/location.svg";
import { getEventTypeLabel } from "../constants/eventTypes";
import { formatDateTime } from "../hooks/DateTime";
import { formatLocation } from "../constants/locations";
import { useJsApiLoader, GoogleMap, MarkerF } from "@react-google-maps/api";
import { mapStyling } from "../components/maps/styling";
import { Link } from "react-router-dom";
import { Blurhash } from "react-blurhash";

interface InfoCardProps {
  icon: string;
  title: string;
  description: string;
  additionalImageClass?: string;
}

const InfoCard: React.FC<InfoCardProps> = ({
  icon,
  title,
  description,
  additionalImageClass,
}) => {
  return (
    <div className="grid grid-cols-2">
      <div className="bg-muted-lightSage rounded-lg mt-0 mb-16 ml-12 mr-12">
        <img
          src={icon}
          alt={title.toLowerCase()}
          className={`filter-sage ${additionalImageClass}`}
        />
      </div>
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </div>
  );
};

interface Coordinates {
  lat: number;
  lng: number;
}

const SingleEvent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("id");
  const location = searchParams.get("location");
  const { eventsData, loading, error } = useData();
  // Find the event that matches the ID
  const event = eventsData.find((event) => event.id === eventId);

  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [geocodingError, setGeocodingError] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_API_KEY || "",
  });

  // Modify useEffect for initial setup
  useEffect(() => {
    if (window.google?.maps) {
    }
  }, []);

  // Move geocoding to a separate function that we can call after map loads
  const geocodeAddress = async () => {
    if (!event?.event_postcode || !isLoaded) {
      return;
    }

    try {
      const geocoder = new google.maps.Geocoder();
      const response = await new Promise((resolve, reject) => {
        geocoder.geocode(
          { address: event.event_postcode },
          (results, status) => {
            if (status === "OK" && results?.[0]) {
              resolve(results[0]);
            } else {
              reject(new Error(`Geocoding failed with status: ${status}`));
            }
          }
        );
      });

      const location = response as google.maps.GeocoderResult;
      const newCoordinates = {
        lat: location.geometry.location.lat(),
        lng: location.geometry.location.lng(),
      };
      console.log("Successfully set coordinates:", newCoordinates);
      setCoordinates(newCoordinates);
      setGeocodingError(null);
    } catch (error) {
      console.error("Geocoding error:", error);
      setGeocodingError("Failed to locate address on map");
    }
  };

  // Modify useEffect for geocoding
  useEffect(() => {
    geocodeAddress();
  }, [event?.event_postcode, isLoaded]);

  // Add this useEffect to scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const apiKey = process.env.REACT_APP_API_KEY;
  if (!apiKey) return <div>Google Maps API key not found</div>;

  if (loading) return <div>Loading 😁...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!event) return <div>Event not found</div>;
  if (geocodingError) return <div>Error: {geocodingError}</div>;

  const mapContainerStyle = {
    width: "100%",
    height: "400px",
  };

  const renderBackPill = () => {
    if (!["events", "home", "profile", "practitioners"].includes(location || "")) {
      return null;
    }

    let backPath = "/";
    let pillText = "Back home";

    if (location === "events") {
      backPath = "/events";
      pillText = "Back to search";
    } else if (location === "profile") {
      backPath = "/profile";
      pillText = "Back to profile";
    } else if (location === "practitioners") {
      backPath = `/practitioners/view?id=${event?.providerDetails?.id}`;
      pillText = "Back to practitioner";
    }

    return (
      <Pill
        text={pillText}
        colorClass="bg-white"
        textColor="muted-grey"
        icon="arrow"
        clickable={true}
        path={backPath}
      />
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
      <div className="left hidden lg:block relative">
        <div className="absolute inset-0">
          {event.header_image_blur_hash && (
            <Blurhash
              hash={event.header_image_blur_hash}
              width="100%"
              height="100%"
              resolutionX={32}
              resolutionY={32}
              punch={1}
              className={`
                absolute inset-0 w-full h-full
                ${imageLoaded ? "animate-fade-out" : ""}
              `}
            />
          )}

          <img
            src={event.header_image ? event.header_image : HeroDance}
            alt="an image related to the event"
            loading="lazy"
            className={`
              absolute inset-0
              w-full h-full 
              object-cover 
              ${imageLoaded ? "animate-fade-in" : "opacity-0"}
            `}
            onLoad={() => setTimeout(() => setImageLoaded(true), 100)}
          />
        </div>
      </div>
      <div className="right p-8">
        <div className="grid grid-cols-6">
          <div className="col-span-5">
            {renderBackPill()}
            <h2 className="font-bold text-muted-grey mt-8">
              {event.provider_name.toUpperCase()}
            </h2>
            <h1 className=" container mx-auto pt-0 pb-8 text-2xl md:text-4xl font-bold">
              {event.event_name}
            </h1>
            <p className="mb-8">{event.event_description}</p>
            <Link to={`/practitioners/view?id=${event.providerDetails?.id}`}>
              <p className="mb-8 underline text-links-default hover:text-links-hover hover:text-muted-sage">
                Visit {event.provider_name}'s profile
              </p>
            </Link>
          </div>
          <div className="col-span-1">
            <img
              className="rounded-full h-20 w-20 object-cover"
              src={event.providerDetails?.provider_logo}
              alt="provider logo"
            />
          </div>
        </div>
        <div className="grid grid-cols-2">
          <div>
            {event.event_types?.map((eventType) => (
              <Pill
                extraClass="mr-2"
                text={getEventTypeLabel(eventType) || "Unknown Type"}
                colorClass="bg-muted-sage"
              />
            ))}
          </div>
          <div className="flex justify-end pr-8">
            <Button onClick={() => window.open(event.event_link, "_blank")}>
              Book Now
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 mt-4">
          <h2 className="font-noto text-2xl font-bold mt-8 mb-8">
            Timing and location
          </h2>
        </div>
        <div className="grid grid-cols-2">
          <InfoCard
            icon={CalendarIcon}
            title="Date and time"
            description={formatDateTime(event.date, event.start_time)}
            additionalImageClass="p-4"
          />
          <InfoCard
            icon={LocationIcon}
            title="Location"
            description={formatLocation(event)}
          />
        </div>
        <div className="grid grid-cols-1">
          {isLoaded ? (
            coordinates ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={coordinates}
                zoom={15}
                options={{
                  styles: mapStyling,
                }}
              >
                <MarkerF
                  position={coordinates}
                  onClick={() => {
                    const googleMapsUrl = `https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`;
                    window.open(googleMapsUrl, "_blank");
                  }}
                />
              </GoogleMap>
            ) : (
              <div>Loading map location...</div>
            )
          ) : (
            <div>Loading Google Maps...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleEvent;
